import { useState } from "react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Sparkle } from "@phosphor-icons/react";
import AiScopeDialog from "../quotes/AiScopeDialog";

export const scopeLines = (text) => (text || "").split("\n").map((l) => l.trim()).filter(Boolean);

/* Read-only rendering shared by admin cards and the client portal: "-" lines become bullets. */
export function ScopeText({ text, className = "", testid }) {
  const items = scopeLines(text);
  if (!items.length) return null;
  return (
    <div className={`text-sm space-y-1 ${className}`} data-testid={testid}>
      {items.map((l, i) => (/^[-*•]/.test(l)
        ? <p key={i} className="flex gap-2"><span className="text-muted-foreground">•</span><span>{l.replace(/^[-*•]\s*/, "")}</span></p>
        : <p key={i}>{l}</p>))}
    </div>
  );
}

const PLACEHOLDER = "Describe exactly what the works include, e.g.\n- Isolate supply and remove the failed combi boiler\n- Supply and fit like-for-like replacement with new flue and magnetic filter\n- Commission, gas safety certificate and remove old unit\n- Make good and leave site clean";

/* Textarea + AI draft used in the Job / Job Request create forms and the edit cards. `context` feeds the AI (title, property, client). */
export default function ScopeOfWorksField({ value, onChange, context = {}, rows = 6, testid = "scope-of-works", label = "Scope of Works", hint = "Shown to the client in their portal and printed on works orders. Start a line with \"-\" for a bullet. Leave blank if not needed." }) {
  const [aiOpen, setAiOpen] = useState(false);
  return (
    <div data-testid={`${testid}-field`}>
      <div className={`flex items-start ${label || hint ? "justify-between" : "justify-end"} gap-3 mb-1`}>
        {(label || hint) && (
          <div>
            {label && <p className="text-sm font-medium">{label}</p>}
            {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
          </div>
        )}
        <Button type="button" size="sm" variant="outline" className="h-7 text-xs shrink-0 border-indigo-300 text-indigo-700 hover:bg-indigo-50 dark:border-indigo-700 dark:text-indigo-300 dark:hover:bg-indigo-950/40" onClick={() => setAiOpen(true)} data-testid={`${testid}-ai-btn`}>
          <Sparkle size={13} weight="fill" className="mr-1" /> {value?.trim() ? "AI refine" : "AI draft"}
        </Button>
      </div>
      <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={rows} placeholder={PLACEHOLDER} className="text-xs" data-testid={testid} />
      <AiScopeDialog key={aiOpen ? "open" : "closed"} open={aiOpen} onOpenChange={setAiOpen} kind="scope" entity={context.entity || "job"}
        quote={{ title: context.title || "", description: context.description || "", client_name: context.client_name || "", property_address: context.property_address || "", lines: [] }}
        existingScope={value || ""} noCaveats
        onApply={({ text, mode }) => onChange(mode === "append" && value?.trim() ? `${value.trimEnd()}\n${text}` : text)} />
    </div>
  );
}
